import { useState, useEffect, useRef, useCallback } from 'react';

interface UserInteractionConfig {
  idleTimeoutMs?: number; // Tempo em ms para considerar usuário inativo
  debounceMs?: number; // Debounce para evitar muitos eventos
}

interface UserInteractionReturn {
  isUserActive: boolean;
  lastActivityTime: Date | null;
  resetActivity: () => void;
}

export const useUserInteraction = (
  config: UserInteractionConfig = {}
): UserInteractionReturn => {
  const {
    idleTimeoutMs = 5000, // 5 segundos de inatividade por padrão
    debounceMs = 100 // 100ms de debounce
  } = config;

  const [isUserActive, setIsUserActive] = useState(false);
  const [lastActivityTime, setLastActivityTime] = useState<Date | null>(null);

  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const resetActivity = useCallback(() => {
    setIsUserActive(true);
    setLastActivityTime(new Date());

    // Limpar timer de inatividade anterior
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    // Configurar novo timer de inatividade
    idleTimerRef.current = setTimeout(() => {
      setIsUserActive(false);
    }, idleTimeoutMs);
  }, [idleTimeoutMs]);

  const handleUserActivity = useCallback(() => {
    // Debounce para evitar muitos eventos
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      resetActivity();
    }, debounceMs);
  }, [resetActivity, debounceMs]);

  useEffect(() => {
    // Eventos que indicam atividade do usuário - removendo mousemove para reduzir overhead
    const events = [
      'mousedown',
      'keypress',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'focus',
      'input',
      'change'
    ];

    // Adicionar listeners para todos os eventos
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    // Listener especial para mousemove com throttling mais agressivo
    let mouseMoveThrottle: NodeJS.Timeout | null = null;
    const handleMouseMove = () => {
      if (mouseMoveThrottle) return;
      
      mouseMoveThrottle = setTimeout(() => {
        handleUserActivity();
        mouseMoveThrottle = null;
      }, 500); // Throttle de 500ms para mousemove
    };

    document.addEventListener('mousemove', handleMouseMove, { passive: true });

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });

      document.removeEventListener('mousemove', handleMouseMove);

      if (mouseMoveThrottle) {
        clearTimeout(mouseMoveThrottle);
      }

      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [handleUserActivity]);

  return {
    isUserActive,
    lastActivityTime,
    resetActivity
  };
};